// Twin of zl-api `packages/features/src/auth/sessionWatermark.ts`. Not a copy, and the storage is
// deliberately *not* shared — what has to stay in step is the meaning; see the section below.

/**
 * Who the state stored on *this origin* belongs to.
 *
 * ## Why a watermark at all
 *
 * The session cookie is on `.zoolife.tv`, so signing out — or signing in as someone else — in the
 * educators app ends the session here too. `localStorage` is not shared, so this app has no way to
 * be told about it. It has to notice.
 *
 * Noticing needs memory: "the server says nobody is signed in" is the same answer for a visitor who
 * just logged out elsewhere and for one who has never had an account, and only the first has
 * anything to clean up. The watermark is that memory.
 *
 * This replaced `/login?lo=edu`, where the educators app navigated the browser here so that this
 * app would clean up on its behalf. That only ever fired for the one logout button that remembered
 * to send the flag, and it could not see an account switch at all.
 *
 * ## Why the user id
 *
 * Not the username: it is mutable (`apps/api/src/controllers/users.js`), so a rename would read as
 * a different person and wipe the user's own state. Not a boolean: it would catch a logout but not
 * a switch, and a switch is the case that corrupts data — see `TourSync`.
 *
 * ## Why per-origin storage, not the shared cookie
 *
 * The question is "does the state *I* hold belong to the current user?", which is per-origin by
 * nature. With one shared value, whichever app cleaned up first would rewrite it and every other
 * app would then see no transition and never clean. `sessionStorage` and an in-memory ref both fail
 * a different way: the data being protected outlives the tab, so a watermark that does not is blind
 * to the case that matters.
 *
 * The educators app keeps the same *concept*, and that is all the two have to share. They are on
 * different origins, so their `localStorage` is separate and neither can ever read the other's
 * watermark — the key name and its encoding are private to each app, which is why that one is a
 * jotai atom holding JSON while this writes a bare string. **What must stay in step is the meaning:
 * a watermark naming somebody other than the current user, absent included, is the one and only
 * trigger to forget.**
 *
 * Deliberately a leaf: no app imports, so it stays unit-testable without dragging the application's
 * import graph into a test. The cleanup that acts on this lives in `forgetUser`.
 */
export const WATERMARK_KEY = 'zlSessionWatermark';

/** localStorage throws in private modes and with storage disabled; a watermark is never worth a crash. */
const safely = <T>(fn: () => T, fallback: T): T => {
  try {
    return fn();
  } catch {
    return fallback;
  }
};

export const readSessionWatermark = (): string | null => safely(() => localStorage.getItem(WATERMARK_KEY), null);

export const writeSessionWatermark = (userId: string | null) =>
  safely(() => {
    if (userId) {
      localStorage.setItem(WATERMARK_KEY, userId);
    } else {
      localStorage.removeItem(WATERMARK_KEY);
    }
  }, undefined);

/** The five things an answer can mean, given who the stored state currently belongs to. */
export type SessionTransition = 'still-signed-out' | 'signed-in' | 'refreshed' | 'signed-out' | 'switched';

/**
 * Name what just happened, from the **Session Watermark** and the new answer alone.
 *
 * This is the whole reason the watermark is worth keeping: it already remembers who the stored
 * state belongs to, so it can classify an answer rather than merely flag one. Everything below
 * hangs off the name, and no separate bookkeeping is needed to get there — an earlier version kept
 * a `lastAppliedAt` timestamp and an `identifiedUserId`, both of which were weaker restatements of
 * what the watermark already knows.
 *
 * The distinction that matters most is `still-signed-out` vs `signed-out`. They look identical in
 * the answer — no user either way — and only the watermark tells them apart. Treating them alike is
 * what made `user:logout` fire on every window focus for a signed-out visitor: `dataUpdatedAt`
 * bumps on every successful refetch even when the payload is identical (react-query keeps the same
 * `data` reference but always restamps), so a level-triggered check re-fires transition side
 * effects indefinitely. Here, a visitor with no watermark and no session has had nothing happen to
 * them.
 */
export const classifySessionTransition = (watermark: string | null, userId: string | null): SessionTransition => {
  if (watermark === userId) {
    return userId ? 'refreshed' : 'still-signed-out';
  }
  if (watermark === null) return 'signed-in';
  return userId === null ? 'signed-out' : 'switched';
};

/** Mixpanel's marker for an id it generated for itself, as opposed to one we handed it. */
const ANONYMOUS_TRACKING_PREFIX = '$device:';

/**
 * Does the analytics identity still name somebody who is no longer the session owner?
 *
 * Twin of the educators copy, under the same name. The second half of the Forget, and the one the
 * watermark cannot answer: the watermark is per-origin, but the Mixpanel `distinct_id` is a cookie on
 * `.zoolife.tv` — shared — so "has the tracking identity already been dealt with?" has to be asked of
 * the tracking identity itself.
 *
 * ## Why derived, and not a flag either app writes
 *
 * Mixpanel already keeps exactly the value wanted: the `user._id` once identified, its own
 * `$device:<uuid>` while anonymous, rotated on every reset, shared between the apps. A second copy
 * per app would have to be kept in step with it and nothing could do that. A "cross-site cleanup
 * done" flag is worse: it would make this app skip a reset its own in-memory copy still needs, then
 * write the departed user back over the shared cookie — because persistence loads that cookie only at
 * init and every save rewrites the whole thing from memory.
 *
 * ## The three cases, and why one rule covers them
 *
 * - **A fresh load here after a logout in the educators app.** That app reset, so the cookie already
 *   reads `$device:…` and this one inherited it at init. Nothing is owed, and resetting anyway would
 *   rotate the anonymous id a second time — stranding the browsing done in between, which is the leg
 *   a later signup would otherwise have merged in. The local Forget still runs regardless.
 * - **This app already open when the session ended elsewhere.** Its in-memory copy predates that
 *   reset and still names the departed user, so the reset is owed: otherwise this tab reports the
 *   next person's browsing as the last one's.
 * - **A switch.** The identity names A while the session says B, so the reset runs before the new
 *   user is identified, and no `$identify` ever names A as B's anonymous predecessor.
 *
 * Absent is "nothing owed", which covers Mixpanel not having initialized yet.
 */
export const isTrackingIdentityStale = (trackingId: string | null | undefined, userId: string | null): boolean =>
  !!trackingId && !trackingId.startsWith(ANONYMOUS_TRACKING_PREFIX) && trackingId !== userId;

/**
 * Exact key equality, used to spare the session query from the cache drop.
 *
 * Exact rather than by prefix, deliberately: `['users', 'me', 'offers', id]` shares the session
 * key's prefix, is per-user, and *does* have to go. A prefix match here would leave one user's
 * redeemable offers readable by the next.
 */
export const isSameQueryKey = (a: readonly unknown[], b: readonly unknown[]): boolean =>
  a.length === b.length && a.every((part, i) => part === b[i]);
